
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

interface SeoKeywordUpload {
  page_path: string;
  title: string;
  description: string;
  keywords: string[];
}

const SeoKeywordsBatchUploader = () => {
  const [jsonData, setJsonData] = useState('');
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });
  const [showResults, setShowResults] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonData(e.target.value);
    setShowResults(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonData(content);
      setShowResults(false);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    try {
      if (!isAuthenticated) {
        toast.error('You must be logged in to upload SEO keywords');
        return;
      }
      
      setUploading(true);
      setShowResults(false);
      
      let data: SeoKeywordUpload[];
      
      try {
        data = JSON.parse(jsonData);
        if (!Array.isArray(data)) {
          data = [data]; // Convert to array if it's a single object
        }
      } catch (error) {
        toast.error('Invalid JSON format');
        setUploading(false);
        return;
      }
      
      let successCount = 0;
      let failedCount = 0;
      
      for (const item of data) {
        if (!item.page_path || !item.title || !item.description || !Array.isArray(item.keywords)) {
          failedCount++;
          continue;
        }
        
        const { error } = await supabase
          .from('seo_keywords')
          .upsert({
            page_path: item.page_path,
            title: item.title,
            description: item.description,
            keywords: item.keywords
          }, { onConflict: 'page_path' });
          
        if (error) {
          console.error('Error uploading SEO keyword:', error);
          failedCount++;
        } else {
          successCount++;
        }
      }
      
      setResults({ success: successCount, failed: failedCount });
      setShowResults(true);
      
      if (failedCount === 0) {
        toast.success(`Successfully uploaded ${successCount} keyword entries`);
      } else {
        toast.warning(`Uploaded ${successCount} entries, but ${failedCount} failed`);
      }
    } catch (error) {
      console.error('Error in batch upload:', error);
      toast.error('An error occurred during batch upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <ProtectedRoute>
      <Card>
        <CardHeader>
          <CardTitle>Batch Upload SEO Keywords</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Upload JSON File</label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0 file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Or Paste JSON Data</label>
              <Textarea
                value={jsonData}
                onChange={handleJsonChange}
                placeholder='[{ "page_path": "/example", "title": "Example Title", "description": "Example description", "keywords": ["keyword1", "keyword2"] }]'
                className="font-mono text-sm"
                rows={10}
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: JSON array of objects with page_path, title, description, and keywords array
              </p>
            </div>
            
            <Button 
              onClick={handleUpload} 
              disabled={!jsonData.trim() || uploading}
              className="w-full"
            >
              {uploading ? 'Uploading...' : 'Upload Keywords'}
            </Button>
            
            {showResults && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md border">
                <h3 className="font-semibold mb-1">Upload Results:</h3>
                <p>Successfully uploaded: {results.success}</p>
                <p>Failed: {results.failed}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ProtectedRoute>
  );
};

export default SeoKeywordsBatchUploader;
